<template>
  <div v-if="!hasPermission('List Contract')">Você não tem permissão para acessar esta página!</div>
  <div v-else>
    <Toolbar>
      <template v-slot:actions>
        <div class="d-flex align-items-center flex-wrap">
        <router-link to="/plans/contract-templates" class="btn btn-primary btn-sm me-2">
          <i class="bi bi-file-earmark-richtext me-1"></i>
          Modelos
        </router-link>
        </div>
      </template>
    </Toolbar>
  
  <div class="container-xxl">
    <!-- Dashboard Cards -->
    <div class="row g-4 mb-5">
      <div class="col-xl-4 col-md-6">
        <div class="card hoverable card-xl-stretch border-0 shadow-sm">
          <div class="card-body d-flex align-items-center">
            <div class="icon-box rounded-circle bg-light-primary me-4">
              <i class="bi bi-file-earmark-text fs-2 text-primary"></i>
            </div>
            <div>
              <div class="text-gray-900 fw-bold fs-2">{{ dashboard.total || 0 }}</div>
              <div class="text-gray-500 fw-medium fs-7">Total de Contratos</div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-xl-4 col-md-6">
        <div class="card hoverable card-xl-stretch border-0 shadow-sm">
          <div class="card-body d-flex align-items-center">
            <div class="icon-box rounded-circle bg-light-warning me-4">
              <i class="bi bi-hourglass-split fs-2 text-warning"></i>
            </div>
            <div>
              <div class="text-gray-900 fw-bold fs-2">{{ dashboard.pending || 0 }}</div>
              <div class="text-gray-500 fw-medium fs-7">Pendentes</div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-xl-4 col-md-6">
        <div class="card hoverable card-xl-stretch border-0 shadow-sm">
          <div class="card-body d-flex align-items-center">
            <div class="icon-box rounded-circle bg-light-danger me-4">
              <i class="bi bi-x-circle fs-2 text-danger"></i>
            </div>
            <div>
              <div class="text-gray-900 fw-bold fs-2">{{ dashboard.cancelled + dashboard.expired || 0 }}</div>
              <div class="text-gray-500 fw-medium fs-7">Cancelados/Expirados</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Contracts List -->
    <div class="card">
      <div class="card-header">
        <div class="card-title">
          <div class="d-flex align-items-center position-relative my-1">
            <span class="svg-icon svg-icon-1 position-absolute ms-6">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <rect opacity="0.5" x="17.0365" y="15.1223" width="8.15546" height="2" rx="1"
                      transform="rotate(45 17.0365 15.1223)" fill="black"></rect>
                <path
                  d="M11 19C6.55556 19 3 15.4444 3 11C3 6.55556 6.55556 3 11 3C15.4444 3 19 6.55556 19 11C19 15.4444 15.4444 19 11 19ZM11 5C7.53333 5 5 7.53333 5 11C5 14.4667 7.53333 17 11 17C14.4667 17 17 14.4667 17 11C17 7.53333 14.4667 5 11 5Z"
                  fill="black"></path>
              </svg>
            </span>
            <input type="search" v-model="search"
                   class="form-control form-control-solid w-250px ps-14"
                   placeholder="Pesquisar...">
          </div>
        </div>
        <div class="card-toolbar">
          <div class="d-flex gap-2">
            <select class="form-select form-select-solid w-150px" v-model="filterStatus">
              <option value="">Todos os status</option>
              <option value="pending">Pendente</option>
              <option value="cancelled">Cancelado</option>
              <option value="expired">Expirado</option>
            </select>
            <select class="form-select form-select-solid w-200px" v-model="filterPlanId">
              <option value="">Todos os planos</option>
              <option v-for="plan in plans" :key="plan.id_plan" :value="plan.id_plan">
                {{ plan.plan_name }}
              </option>
            </select>
          </div>
        </div>
      </div>
      <div class="card-body py-4">
        <div class="table-responsive">
          <table class="table table-hover table-row-bordered">
            <thead>
            <tr class="fw-bold fs-6 text-muted">
              <th>Assinante</th>
              <th>Plano</th>
              <th>Modelo</th>
              <th>Número</th>
              <th>Status</th>
              <th>Válido até</th>
              <th class="text-center">Ações</th>
            </tr>
            </thead>
            <tbody>
            <template v-for="contract in contracts.data" :key="contract.id_contract">
              <tr>
                <td>
                  <div class="d-flex flex-column">
                    <span class="text-gray-800 text-hover-primary fw-bold">
                      {{ contract.subscriber?.user?.user_name || '-' }}
                    </span>
                    <span class="text-muted small">
                      {{ contract.subscriber?.user?.document_number || contract.subscriber?.user?.user_cpf || '-' }}
                    </span>
                  </div>
                </td>
                <td>{{ contract.subscriber?.plan?.plan_name || '-' }}</td>
                <td>{{ contract.template?.template_name || '-' }}</td>
                <td>
                  <code class="text-muted small">{{ contract.contract_number || '-' }}</code>
                </td>
                <td>
                  <span :class="getStatusBadgeClass(contract.contract_status)">
                    {{ contract.contract_status_dsc }}
                  </span>
                </td>
                <td>{{ contract.valid_until ? date(contract.valid_until).format('DD/MM/YYYY') : '-' }}</td>
                <td class="text-center" style="min-width: 150px;">
                  <button class="btn btn-sm btn-icon btn-bg-light btn-active-color-primary me-1 border border-gray-400"
                          type="button" @click="viewContract(contract)"
                          title="Visualizar contrato">
                    <i class="bi bi-eye"></i>
                  </button>
                  <button class="btn btn-sm btn-icon btn-bg-light btn-active-color-success me-1 border border-gray-400"
                          type="button" @click="downloadContract(contract)"
                          title="Download PDF">
                    <i class="bi bi-download"></i>
                  </button>
                  <button v-if="canCancelFromListing(contract)"
                          class="btn btn-sm btn-icon btn-bg-light btn-active-color-danger me-1 border border-gray-400"
                          type="button" @click="cancelContract(contract)"
                          title="Cancelar contrato">
                    <i class="bi bi-x-circle"></i>
                  </button>
                </td>
              </tr>
              <!-- Linha de detalhes do cancelamento -->
              <tr v-if="contract.contract_status === 'cancelled'">
                <td colspan="7" class="bg-light">
                  <div class="p-3 small text-muted">
                    <div class="row">
                      <div class="col-md-6">
                        <strong>Cancelado por:</strong> {{ contract.cancelled_by?.user_name || '-' }}
                      </div>
                      <div class="col-md-6">
                        <strong>Data do cancelamento:</strong> {{ contract.cancelled_at ? date(contract.cancelled_at).format('DD/MM/YYYY HH:mm') : '-' }}
                      </div>
                    </div>
                    <div class="row mt-2" v-if="contract.cancellation_reason">
                      <div class="col-12">
                        <strong>Motivo:</strong>
                        <div class="mt-1 p-2 bg-white rounded border">{{ contract.cancellation_reason }}</div>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            </template>
            <tr v-if="!contracts.data?.length">
              <td colspan="7" class="text-center text-muted py-5">
                Nenhum contrato encontrado.
              </td>
            </tr>
            </tbody>
          </table>

          <Pagination :data="contracts"/>
        </div>
      </div>
    </div>
  </div>
  </div>
</template>

<script>
import http from "../../helpers/http";
import Pagination from "../../components/Pagination";
import Toolbar from "../../components/layouts/auth/Toolbar.vue";
import Alert from "@/helpers/alert";
import Download from "@/helpers/download";
import Loader from "@/helpers/loader";
import Permission from "@/helpers/permission";

export default {
  name: "Contract",
  components: {
    Pagination,
    Toolbar,
  },
  data() {
    return {
      search: '',
      filterStatus: '',
      filterPlanId: '',
      contracts: [],
      plans: [],
      dashboard: {
        total: 0,
        pending: 0,
        cancelled: 0,
        expired: 0,
      },
    }
  },
  computed: {
    place() {
      return this.$store.state.auth.place;
    },
  },
  mounted() {
    this.resetPage();
    this.loadDashboard();
    this.loadContracts();
    this.loadPlans();
  },
  watch: {
    '$route.query.page'() {
      this.loadContracts();
    },
    search() {
      this.loadContracts();
    },
    filterStatus() {
      this.loadContracts();
    },
    filterPlanId() {
      this.loadContracts();
    },
  },
  methods: {
    date: moment,
    loadDashboard() {
      http.get(`/places/${this.place.id_place}/contracts/dashboard`)
        .then((response) => {
          this.dashboard = response.data;
        });
    },
    loadContracts() {
      this.contracts = [];
      http.get(`/places/${this.place.id_place}/contracts`, {
        params: {
          page: this.$route.query.page,
          search: this.search,
          status: this.filterStatus,
          plan_id: this.filterPlanId,
          per_page: 10,
        }
      }).then((response) => {
        this.contracts = response.data;
      });
    },
    loadPlans() {
      http.get(`/places/${this.place.id_place}/plans`, {
        params: { per_page: 100 }
      }).then((response) => {
        this.plans = response.data.data || response.data;
      });
    },
    viewContract(contract) {
      Loader.show('Carregando contrato, por favor aguarde...');

      http.get(`/places/${this.place.id_place}/contracts/${contract.id_contract}/download`, {
        params: { inline: 1 },
        responseType: 'blob'
      })
      .then((response) => {
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
        Loader.close();
        // Limpar URL após um tempo
        setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      })
      .catch((error) => {
        console.error('Erro ao visualizar contrato:', error);
        Loader.close();
        Alert.error('Erro ao visualizar contrato');
      });
    },
    downloadContract(contract) {
      Loader.show('Baixando contrato, por favor aguarde...');

      http.get(`/places/${this.place.id_place}/contracts/${contract.id_contract}/download`, {
        responseType: 'blob'
      })
      .then((response) => {
        Download.downloadFile(response.data, `contrato-${contract.subscriber?.user?.user_name || contract.id_contract}.pdf`, 'application/pdf');
        Loader.close();
      })
      .catch((error) => {
        console.error('Erro ao baixar contrato:', error);
        Loader.close();
        Alert.error('Erro ao baixar contrato');
      });
    },
    canCancelFromListing(contract) {
      return contract.contract_status === 'pending' && !contract.id_company;
    },
    cancelContract(contract) {
      if (!this.canCancelFromListing(contract)) return;
      Swal.fire({
        title: 'Cancelar Contrato',
        html: `
          <div class="text-start">
            <p class="mb-3">Tem certeza que deseja cancelar este contrato?</p>
            <label for="cancellation_reason" class="form-label fw-bold">Motivo do cancelamento *</label>
            <textarea
              id="cancellation_reason"
              class="form-control"
              rows="3"
              placeholder="Digite o motivo do cancelamento..."
              maxlength="1000"
            ></textarea>
            <div class="form-text">Máximo 1000 caracteres</div>
          </div>
        `,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sim, cancelar!',
        cancelButtonText: 'Não',
        customClass: {
          confirmButton: "btn btn-danger",
          cancelButton: 'btn btn-secondary'
        },
        preConfirm: () => {
          const reason = document.getElementById('cancellation_reason').value.trim();
          if (!reason) {
            Swal.showValidationMessage('O motivo do cancelamento é obrigatório');
            return false;
          }
          return reason;
        }
      }).then((result) => {
        if (result.isConfirmed && result.value) {
          http.post(`/places/${this.place.id_place}/contracts/${contract.id_contract}/cancel`, {
            cancellation_reason: result.value
          })
            .then(() => {
              this.loadContracts();
              this.loadDashboard();
              Alert.success('Contrato cancelado com sucesso!');
            })
            .catch((e) => {
              Alert.error(e.response?.data?.message || e.response?.data || 'Erro ao cancelar contrato');
            });
        }
      });
    },
    getStatusBadgeClass(status) {
      const classes = {
        'Pendente': 'badge badge-warning',
        'Ativo': 'badge badge-success',
        'Cancelado': 'badge badge-danger',
        'Expirado': 'badge badge-secondary',
      };
      return classes[status] || 'badge badge-light';
    },
    resetPage() {
      const query = {...this.$route.query, page: 1};
      this.$router.push({path: this.$route.path, query});
    },
    hasPermission(permission) {
      return Permission.has(permission);
    }
  }
}
</script>

<style scoped>
.icon-box {
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.bg-light-primary {
  background-color: rgba(0, 158, 247, 0.1) !important;
}

.bg-light-warning {
  background-color: rgba(255, 199, 0, 0.1) !important;
}

.bg-light-success {
  background-color: rgba(80, 205, 137, 0.1) !important;
}

.bg-light-danger {
  background-color: rgba(241, 65, 108, 0.1) !important;
}

.card.hoverable {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.card.hoverable:hover {
  transform: translateY(-2px);
  box-shadow: 0 0.5rem 1.5rem rgba(0, 0, 0, 0.08) !important;
}
</style>

